import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { NewsItem, NewsResponse } from '../types';

interface NewsTickerProps {
  className?: string;
}

export const NewsTicker: React.FC<NewsTickerProps> = ({ className = '' }) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (news.length > 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      let scrollPosition = 0;
      const scrollSpeed = 0.5; // pixels per frame (slower for readability)
      
      const scrollInterval = setInterval(() => {
        if (container) {
          scrollPosition += scrollSpeed;
          const maxScroll = container.scrollWidth - container.clientWidth;
          
          if (maxScroll > 0) {
            if (scrollPosition >= maxScroll) {
              scrollPosition = 0; // Reset to beginning
            }
            container.scrollLeft = scrollPosition;
          }
        }
      }, 16); // 60fps for smooth scrolling
      
      return () => {
        clearInterval(scrollInterval);
      };
    }
  }, [news.length]);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: NewsResponse = await apiService.getNewsHeadlines(20);
      // Sort by created_at in descending order (latest first)
      const sortedNews = response.data.sort((a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setNews(sortedNews);
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestampToEST = (isoString: string) => {
    const date = new Date(isoString);
    // Convert to EST (UTC-5, or UTC-4 during daylight saving)
    const estOffset = date.getMonth() >= 2 && date.getMonth() <= 10 ? -4 : -5;
    const estDate = new Date(date.getTime() + (estOffset * 60 * 60 * 1000));
    return estDate.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      hour12: false,
    });
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return '#10b981';
      case 'negative':
        return '#ef4444';
      case 'neutral':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    setScrollPosition(container.scrollLeft);
  };

  if (loading) {
    return (
      <div className={`news-ticker ${className}`}>
        <div className="news-ticker-content">
          <div className="news-ticker-label">📰 MARKET NEWS</div>
          <div className="news-ticker-item loading">
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span>Loading latest market news...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return (
      <div className={`news-ticker ${className}`}>
        <div className="news-ticker-content">
          <div className="news-ticker-label">📰 MARKET NEWS</div>
          <div className="news-ticker-item error">
            <span>No news available</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`news-ticker ${className}`}
      role="region"
      aria-label="Market news ticker"
    >
      <div className="news-ticker-content">
        <div className="news-ticker-label">📰 MARKET NEWS</div>
        
        {/* Scrollable News Container */}
        <div 
          className="news-ticker-scroll-container"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div className="news-ticker-items">
            {news.map((item, index) => (
              <div key={index} className="news-ticker-item">
                <div className="news-ticker-main">
                  <span className="news-ticker-source">
                    {item.source}
                    {item.is_major && <span className="major-indicator">●</span>}
                  </span>
                  <span className="news-ticker-headline">{item.headline}</span>
                  <div className="news-ticker-meta">
                    <span className="news-ticker-time">{formatTimestampToEST(item.created_at)}</span>
                    <span 
                      className="news-ticker-sentiment" 
                      style={{ color: getSentimentColor(item.sentiment) }}
                    >
                      {item.sentiment}
                    </span>
                    {item.tickers.length > 0 && (
                      <div className="news-ticker-tickers">
                        {item.tickers.slice(0, 3).map((ticker, tIndex) => (
                          <span key={tIndex} className="ticker-chip">{ticker}</span>
                        ))}
                        {item.tickers.length > 3 && (
                          <span className="ticker-more">+{item.tickers.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="news-ticker-separator">•</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="news-ticker-progress">
          <div 
            className="news-ticker-progress-bar"
            style={{ 
              width: `${news.length > 0 ? ((scrollPosition / (scrollContainerRef.current?.scrollWidth || 1)) * 100) : 0}%` 
            }}
          />
        </div>
      </div>
      
    </div>
  );
};
